import {
  deleteInsurance,
  editInsurance,
  getAllInsurances,
  getEquipmentsByPolicyId,
  removeEquipmentFromInsurance
} from 'src/services/insuranceService';
import { asyncActionCreator } from 'src/utils/loadingUtils';
import { SnackBarVariants } from 'src/utils/constants/common';
import uploadSingleFile from './commonActions';
import { showMessage } from './snackBarActions';
import {
  createNewInsurance,
  getInsuranceById
} from '../../services/insuranceService';
import { selectInsuranceQueryOptions } from '../selectors/insuranceSelector';

export const SAVE_INSURANCE_OPTIONS = 'SAVE_INSURANCE_OPTIONS';
export const SAVE_EQUIPMENT_OPTIONS = 'SAVE_EQUIPMENT_OPTIONS';
export const CLEAR_INSURANCE_OPTIONS = 'CLEAR_INSURANCE_OPTIONS';
export const CLEAR_EQUIPMENT_OPTIONS = 'CLEAR_EQUIPMENT_OPTIONS';
export const LOAD_INSURANCES = asyncActionCreator('LOAD_INSURANCES');
export const CREATE_INSURANCE = asyncActionCreator('CREATE_INSURANCE');
export const UPDATE_INSURANCE = asyncActionCreator('UPDATE_INSURANCE');
export const DELETE_INSURANCE = asyncActionCreator('DELETE_INSURANCE');
export const REMOVE_EQUIPMENT_FROM_INSURANCE = asyncActionCreator(
  'REMOVE_EQUIPMENT_FROM_INSURANCE'
);
export const LOAD_INSURANCE_DETAIL = asyncActionCreator(
  'LOAD_INSURANCE_DETAIL'
);
export const LOAD_INSURANCE_EQUIPMENTS = asyncActionCreator(
  'LOAD_INSURANCE_EQUIPMENTS'
);
export const CLEAR_INSURANCE_EQUIPMENTS = 'CLEAR_INSURANCE_EQUIPMENTS';
export const CLEAR_INSURANCE_DETAIL = 'CLEAR_INSURANCE_DETAIL';
export const CLEAR_INSURANCES = 'CLEAR_INSURANCES';

function getOptions(getState, sendLimit = true) {
  const queryOpts = selectInsuranceQueryOptions(getState());

  const reqOptions = {};

  if (queryOpts.page > 0) {
    reqOptions.offset =
      queryOpts.page * Number(process.env.REACT_APP_TABLE_SIZE);
  }
  if (queryOpts.equipmentId) {
    reqOptions.equipmentId = queryOpts.equipmentId;
  }
  if (queryOpts.equipmentName) {
    reqOptions.equipmentName = queryOpts.equipmentName;
  }
  if (queryOpts.description) {
    reqOptions.description = queryOpts.description;
  }
  if (queryOpts.id) {
    reqOptions.policyId = queryOpts.id;
  }
  if (queryOpts.clientName) {
    reqOptions.clientName = queryOpts.clientName;
  }
  if (queryOpts.remainingDays) {
    reqOptions.remainingDays = queryOpts.remainingDays;
  }
  if (queryOpts.sort) {
    reqOptions.sortOrder = queryOpts.sort.order;
    reqOptions.sortProperty = queryOpts.sort.property;
  }
  if (sendLimit) {
    reqOptions.limit = queryOpts.limit;
  }

  return reqOptions;
}

function getEquipmentOptions(getState, sendLimit = true) {
  const { equipmentOptions } = getState().insurances;
  const reqOptions = { ...equipmentOptions };

  if (equipmentOptions.page > 0) {
    reqOptions.offset =
      equipmentOptions.page * Number(process.env.REACT_APP_TABLE_SIZE);
  }

  if (!sendLimit) {
    delete reqOptions.limit;
  } else if (!reqOptions.limit) {
    reqOptions.limit = Number(process.env.REACT_APP_TABLE_SIZE);
  }

  Object.keys(reqOptions).forEach(key => {
    if (reqOptions[key] === '') {
      delete reqOptions[key];
    }
  });

  return reqOptions;
}

export const clearOptions = () => dispatch => {
  dispatch({ type: CLEAR_INSURANCE_OPTIONS });
};

export const clearEquipmentOptions = () => dispatch => {
  dispatch({ type: CLEAR_EQUIPMENT_OPTIONS });
};

export const clearClients = () => async dispatch => {
  try {
    return dispatch({ type: CLEAR_INSURANCES });
  } catch (error) {
    return dispatch(
      showMessage({ message: error.message, variant: SnackBarVariants.ERROR })
    );
  }
};

export const clearInsuranceEquipments = () => dispatch => {
  dispatch({ type: CLEAR_INSURANCE_EQUIPMENTS });
};

export const loadInsurances = options => async (dispatch, getState) => {
  try {
    dispatch({ type: LOAD_INSURANCES.start });

    dispatch({
      type: SAVE_INSURANCE_OPTIONS,
      options
    });

    const reqOptions = getOptions(getState);
    const data = await getAllInsurances(reqOptions);

    return dispatch({
      type: LOAD_INSURANCES.success,
      ...data
    });
  } catch (error) {
    dispatch(
      showMessage({ message: error.message, variant: SnackBarVariants.ERROR })
    );
    return dispatch({ type: LOAD_INSURANCES.failure });
  }
};

export const loadInsuranceDetail = policyId => async dispatch => {
  try {
    dispatch({ type: LOAD_INSURANCE_DETAIL.start });
    const data = await getInsuranceById(policyId);
    return dispatch({
      type: LOAD_INSURANCE_DETAIL.success,
      data
    });
  } catch (error) {
    dispatch(
      showMessage({ message: error.message, variant: SnackBarVariants.ERROR })
    );
    return dispatch({ type: LOAD_INSURANCE_DETAIL.failure });
  }
};

export const createInsurance = values => async dispatch => {
  try {
    dispatch({ type: CREATE_INSURANCE.start });
    let { policyPdfUrl } = values;

    if (policyPdfUrl && policyPdfUrl.inDb) {
      policyPdfUrl = '';
    }

    if (policyPdfUrl) {
      policyPdfUrl = await uploadSingleFile(policyPdfUrl);
    }

    const data = await createNewInsurance({
      ...values,
      insurerId: values.insurerId.key,
      agencyId: values.agencyId.key,
      policyPdfUrl
    });

    dispatch(
      showMessage({
        message: 'CREATE_INSURANCE',
        variant: 'success'
      })
    );

    dispatch(loadInsurances());

    return dispatch({
      type: CREATE_INSURANCE.success,
      data
    });
  } catch (error) {
    dispatch(
      showMessage({ message: error.message, variant: SnackBarVariants.ERROR })
    );
    return dispatch({ type: CREATE_INSURANCE.failure });
  }
};

export const updateInsurance = (
  policyId,
  values,
  callback
) => async dispatch => {
  try {
    dispatch({ type: UPDATE_INSURANCE.start });
    let { policyPdfUrl } = values;

    if (policyPdfUrl && policyPdfUrl.inDb) {
      policyPdfUrl = '';
    }

    if (policyPdfUrl) {
      policyPdfUrl = await uploadSingleFile(policyPdfUrl);
    }

    await editInsurance({
      policyId,
      insurance: {
        ...values,
        policyPdfUrl,
        iva: Number(values.iva),
        agencyId: values.agencyId.key,
        insurerId: values.insurerId.key
      }
    });

    dispatch(
      showMessage({
        message: 'UPDATE_INSURANCE',
        variant: SnackBarVariants.SUCCESS
      })
    );

    callback();

    return dispatch({
      type: UPDATE_INSURANCE.success
    });
  } catch (error) {
    dispatch(
      showMessage({ message: error.message, variant: SnackBarVariants.ERROR })
    );
    return dispatch({ type: UPDATE_INSURANCE.failure });
  }
};

export const deleteInsuranceAction = policyId => async dispatch => {
  try {
    dispatch({ type: DELETE_INSURANCE.start });
    const data = await deleteInsurance(policyId);

    dispatch(
      showMessage({
        message: 'DELETE_INSURANCE',
        variant: 'success'
      })
    );
    dispatch(loadInsurances());
    return dispatch({
      type: DELETE_INSURANCE.success,
      data
    });
  } catch (error) {
    dispatch(
      showMessage({ message: error.message, variant: SnackBarVariants.ERROR })
    );
    return dispatch({ type: DELETE_INSURANCE.failure });
  }
};

export const loadInsuranceEquipments = options => async (
  dispatch,
  getState
) => {
  try {
    dispatch({ type: LOAD_INSURANCE_EQUIPMENTS.start });

    const optionsWithLimit = {
      limit: Number(process.env.REACT_APP_TABLE_SIZE),
      page: 0,
      ...options
    };

    dispatch({
      type: SAVE_EQUIPMENT_OPTIONS,
      options: optionsWithLimit
    });

    const reqOptions = getEquipmentOptions(getState);
    const data = await getEquipmentsByPolicyId({
      policyId: options.policyId,
      reqOptions
    });
    return dispatch({
      type: LOAD_INSURANCE_EQUIPMENTS.success,
      data
    });
  } catch (error) {
    dispatch(
      showMessage({ message: error.message, variant: SnackBarVariants.ERROR })
    );
    return dispatch({ type: LOAD_INSURANCE_EQUIPMENTS.failure });
  }
};

export const removeEquipmentFromInsuranceAction = (
  policyId,
  equipmentsIds,
  onClose
) => async dispatch => {
  try {
    dispatch({ type: REMOVE_EQUIPMENT_FROM_INSURANCE.start });
    const data = await removeEquipmentFromInsurance({
      policyId,
      equipmentsIds
    });

    dispatch(
      showMessage({
        message: 'REMOVE_EQUIPMENT_FROM_INSURANCE',
        variant: SnackBarVariants.SUCCESS
      })
    );
    dispatch(loadInsuranceEquipments({ policyId }));
    onClose();
    return dispatch({
      type: REMOVE_EQUIPMENT_FROM_INSURANCE.success,
      data
    });
  } catch (error) {
    dispatch(
      showMessage({ message: error.message, variant: SnackBarVariants.ERROR })
    );
    return dispatch({ type: REMOVE_EQUIPMENT_FROM_INSURANCE.failure });
  }
};
